import { BackendError, type BackendClient, type Session, type Unfurl, type UploadResult, type User, type WorkspaceInfo } from './backend';
import { newId } from './ids';
import { seedWorkspace } from './seed';
import { slugify } from './slug';
import type { Workspace } from './workspace';

/** Synchronous key/value storage (the platform's `storage` adapter fits). */
export interface LocalStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

interface StoredUser extends User {
  salt: string;
  hash: string;
}

interface StoredWorkspace {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  owner: string;
}

interface LocalData {
  users: Record<string, StoredUser>;
  workspaces: Record<string, StoredWorkspace>;
  sessions: Record<string, string>;
}

const KEY = 'lumen-local:v1';
const SERVER_ONLY = 'This needs a Lumen server. In local mode everything stays in this browser.';

async function hashPassword(password: string, salt: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: new TextEncoder().encode(salt), iterations: 100_000 }, key, 256);
  return [...new Uint8Array(bits)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function blobToDataUrl(blob: Blob, type: string): Promise<string> {
  return blob.arrayBuffer().then((buf) => {
    let binary = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return `data:${type || blob.type || 'application/octet-stream'};base64,${btoa(binary)}`;
  });
}

/**
 * A backend that lives entirely in the browser, for static hosting without the Lumen server (e.g. Vercel).
 * Accounts and workspaces are kept in local storage, documents in IndexedDB through the platform's
 * persistence; there is no sync, sharing, publishing or version history. Uploads become data URLs.
 */
export class LocalBackend implements BackendClient {
  readonly localOnly = true;
  private readonly open = new Map<string, Workspace>();

  constructor(private readonly store: LocalStore) {}

  private load(): LocalData {
    try {
      const raw = this.store.get(KEY);
      if (raw) return JSON.parse(raw) as LocalData;
    } catch {
      // Unreadable data: start over.
    }
    return { users: {}, workspaces: {}, sessions: {} };
  }

  private save(data: LocalData) {
    this.store.set(KEY, JSON.stringify(data));
  }

  private userFor(data: LocalData, token: string | null): StoredUser {
    const id = token ? data.sessions[token] : undefined;
    const user = id ? data.users[id] : undefined;
    if (!user) throw new BackendError(401, 'Please sign in again.');
    return user;
  }

  private session(data: LocalData, token: string, user: StoredUser): Session {
    const workspaces: WorkspaceInfo[] = Object.values(data.workspaces)
      .filter((w) => w.owner === user.id)
      .map((w) => ({ id: w.id, name: w.name, slug: w.slug, icon: w.icon, role: 'owner' }));
    return { token, user: { id: user.id, email: user.email, name: user.name, avatar: user.avatar }, workspaces };
  }

  private addWorkspace(data: LocalData, owner: string, name: string): StoredWorkspace {
    const id = newId();
    let slug = slugify(name) || 'workspace';
    if (Object.values(data.workspaces).some((w) => w.slug === slug)) slug = `${slug}-${id.slice(0, 6)}`;
    const w = { id, name, slug, icon: null, owner };
    data.workspaces[id] = w;
    return w;
  }

  private startSession(data: LocalData, user: StoredUser): Session {
    const token = newId() + newId();
    data.sessions[token] = user.id;
    this.save(data);
    return this.session(data, token, user);
  }

  async signUp(input: { email: string; password: string; name: string }): Promise<Session> {
    const data = this.load();
    const email = input.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+$/.test(email)) throw new BackendError(400, 'Please enter a valid email address.');
    if (input.password.length < 8) throw new BackendError(400, 'Password must be at least 8 characters.');
    if (Object.values(data.users).some((u) => u.email === email)) throw new BackendError(409, 'An account with this email already exists.');
    const salt = newId();
    const name = input.name.trim() || email.split('@')[0]!;
    const user: StoredUser = { id: newId(), email, name, avatar: null, salt, hash: await hashPassword(input.password, salt) };
    data.users[user.id] = user;
    this.addWorkspace(data, user.id, `${name.split(' ')[0]}'s Workspace`);
    return this.startSession(data, user);
  }

  async signIn(input: { email: string; password: string }): Promise<Session> {
    const data = this.load();
    const user = Object.values(data.users).find((u) => u.email === input.email.trim().toLowerCase());
    if (!user || (await hashPassword(input.password, user.salt)) !== user.hash) throw new BackendError(401, 'Incorrect email or password.');
    return this.startSession(data, user);
  }

  async me(token: string): Promise<Session> {
    const data = this.load();
    return this.session(data, token, this.userFor(data, token));
  }

  async signOut(token: string): Promise<void> {
    const data = this.load();
    delete data.sessions[token];
    this.save(data);
  }

  async createWorkspace(token: string, name: string): Promise<WorkspaceInfo> {
    const data = this.load();
    const user = this.userFor(data, token);
    const w = this.addWorkspace(data, user.id, name.trim() || 'Untitled workspace');
    this.save(data);
    return { id: w.id, name: w.name, slug: w.slug, icon: w.icon, role: 'owner' };
  }

  /** Called once a workspace doc has loaded from local persistence: seeds it the first time. */
  workspaceOpened(workspaceId: string, ws: Workspace): void {
    this.open.set(workspaceId, ws);
    if (ws.meta.get('name') !== undefined) return;
    const data = this.load();
    const w = data.workspaces[workspaceId];
    const owner = w ? data.users[w.owner] : undefined;
    if (!w || !owner) return;
    seedWorkspace(ws, { name: w.name, slug: w.slug, owner: { id: owner.id, name: owner.name, email: owner.email, avatar: owner.avatar } });
  }

  async upload(token: string, file: { name: string; type: string; blob: Blob }): Promise<UploadResult> {
    this.userFor(this.load(), token);
    return { url: await blobToDataUrl(file.blob, file.type), name: file.name, size: file.blob.size, type: file.type };
  }

  async unfurl(_token: string, url: string): Promise<Unfurl> {
    let host = url;
    try {
      host = new URL(url).hostname;
    } catch {
      throw new BackendError(400, 'Invalid URL');
    }
    return { url, title: host, description: '', icon: null, image: null };
  }

  async fetchFile(_token: string, url: string): Promise<Blob> {
    const res = await fetch(url);
    if (!res.ok) throw new BackendError(res.status, 'download failed');
    return res.blob();
  }

  async request<T>(token: string | null, method: string, path: string, body?: unknown): Promise<T> {
    // No server means no server-side config (P-625): the static build takes its download links from the build env.
    if (path === '/api/config') return { downloads: { mac: null, ios: null } } as T;
    return (await this.route(token, method, path, (body ?? {}) as Record<string, unknown>)) as T;
  }

  private async route(token: string | null, method: string, fullPath: string, body: Record<string, unknown>): Promise<unknown> {
    const path = fullPath.split('?')[0]!;
    const data = this.load();
    const user = this.userFor(data, token);

    if (path === '/api/me' && method === 'GET') return this.session(data, token!, user);
    if (path === '/api/me' && method === 'PATCH') {
      if (body.email !== undefined || body.password !== undefined) {
        if ((await hashPassword(String(body.currentPassword ?? ''), user.salt)) !== user.hash) throw new BackendError(403, 'Your current password is incorrect.');
      }
      if (body.name !== undefined) {
        const name = String(body.name).trim();
        if (!name) throw new BackendError(400, 'Please enter a name.');
        user.name = name;
      }
      if (body.avatar !== undefined) user.avatar = (body.avatar as string | null) ?? null;
      if (body.email !== undefined) {
        const email = String(body.email).trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+$/.test(email)) throw new BackendError(400, 'Please enter a valid email address.');
        if (Object.values(data.users).some((u) => u.email === email && u.id !== user.id)) throw new BackendError(409, 'An account with this email already exists.');
        user.email = email;
      }
      if (body.password !== undefined) {
        const password = String(body.password);
        if (password.length < 8) throw new BackendError(400, 'Password must be at least 8 characters.');
        user.salt = newId();
        user.hash = await hashPassword(password, user.salt);
      }
      this.save(data);
      for (const ws of this.open.values()) {
        if (ws.members.has(user.id)) ws.members.set(user.id, { id: user.id, name: user.name, email: user.email, role: 'owner', avatar: user.avatar });
      }
      return this.session(data, token!, user);
    }
    if (path === '/api/auth/logout-all' && method === 'POST') {
      for (const [t, id] of Object.entries(data.sessions)) if (id === user.id) delete data.sessions[t];
      this.save(data);
      return {};
    }
    if (path === '/api/workspaces' && method === 'POST') return this.createWorkspace(token!, String(body.name ?? ''));

    const wsMatch = /^\/api\/workspaces\/([^/]+)(\/members.*)?$/.exec(path);
    if (wsMatch) {
      const w = data.workspaces[wsMatch[1]!];
      if (!w || w.owner !== user.id) throw new BackendError(404, 'Workspace not found');
      if (wsMatch[2]) {
        if (method === 'GET') return { members: [{ id: user.id, name: user.name, email: user.email, role: 'owner' }], invites: [] };
        throw new BackendError(400, SERVER_ONLY);
      }
      const ws = this.open.get(w.id);
      if (method === 'DELETE') {
        if (String(body.confirm ?? '') !== w.name) throw new BackendError(400, 'Type the workspace name to confirm.');
        delete data.workspaces[w.id];
        this.save(data);
        return {};
      }
      if (method === 'PATCH') {
        if (body.name !== undefined) {
          w.name = String(body.name).trim() || 'Untitled workspace';
          ws?.meta.set('name', w.name);
        }
        if (body.icon !== undefined) {
          w.icon = (body.icon as string | null) ?? null;
          ws?.meta.set('icon', w.icon);
        }
        if (body.slug !== undefined) {
          const slug = slugify(String(body.slug));
          if (!slug) throw new BackendError(400, 'Please enter a domain.');
          if (Object.values(data.workspaces).some((o) => o.slug === slug && o.id !== w.id)) throw new BackendError(409, 'This domain is already taken.');
          w.slug = slug;
          ws?.meta.set('slug', slug);
        }
        this.save(data);
        return { id: w.id, name: w.name, slug: w.slug, icon: w.icon };
      }
    }

    if (path.startsWith('/api/share/')) {
      if (method === 'GET') {
        return { level: 'full', owner: { id: user.id, name: user.name, email: user.email }, teamspace: null, grants: [], general: null, inheritedFrom: null, overridden: false, invites: [], published: null, publishedVia: null };
      }
      throw new BackendError(400, SERVER_ONLY);
    }
    if ((path === '/api/snapshots' || path === '/api/updates') && method === 'GET') return [];
    // Notifications and follows only matter to other people, of whom local mode has none.
    if (['/api/notify', '/api/reminders', '/api/follow', '/api/inbox/read'].includes(path)) return {};
    throw new BackendError(400, SERVER_ONLY);
  }
}
